/**
 * Mini-apps this customer PAYS for on other people's behalf
 * (redesign phase 3, items A5 + D9).
 *
 * ── Why this matters more than it looks ─────────────────────────────────
 *
 * `app_budgets` lets an app owner fund other people's usage of their app, and
 * **row existence IS the consent**. No row means payer resolution falls back to
 * the acting user — the fail-safe that stops a manifest author spending someone
 * else's money. So a stale row is a live authorization to spend, and until now
 * it was surfaced nowhere: not to the owner, not to us. It is also why a tenant
 * that owns a popular app can have a bill whose cause no screen could explain.
 *
 * ── Three states, deliberately not collapsed ────────────────────────────
 *
 *   no row            not funded — the app's users pay for themselves
 *   `{month: null}`   funded, UNLIMITED — an explicit opt-in
 *   `{month: 0}`      funded but hard-blocked — NOT the same as no row
 *
 * ── Revoke, never set (decision D9) ─────────────────────────────────────
 *
 * Setting a cap COMMITS a tenant to spending on strangers' usage, which is the
 * owner's consent to give and not ours. Removing the row STOPS that spend, so
 * it fails safe in the direction the consent model already prefers — and it is
 * the only lever available if an app runs away out of hours. The repository
 * writes an attributed audit row, so a revocation shows on the Activity tab.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useToast, useConfirm, useBillingCurrency } from '@papan/core-ui/ui'
import { formatMoney } from '@papan/types'
import { budgetApi } from '../../client/budget'

interface FundedApp {
  id: string
  appFolderId: string
  appName: string | null
  caps: { month?: number | null } | null
  periodCostBillingMicro: number
  createdAt: string
}

export function CustomerFundedApps({
  tenantId,
  canEdit,
}: {
  tenantId: string
  canEdit: boolean
}) {
  const { t } = useTranslation('core-ui')
  const moneyOpts = { currency: useBillingCurrency() }
  const toast = useToast()
  const confirm = useConfirm()
  const [apps, setApps] = useState<FundedApp[] | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await budgetApi.getFundedApps(tenantId)
      setApps(res.apps)
    } catch {
      // A tenant with no funded apps is the overwhelmingly common case, and
      // this is a secondary panel — an error banner here would be noise on a
      // page whose primary content loaded fine.
      setApps([])
    }
  }, [tenantId])

  useEffect(() => { load() }, [load])

  // Nothing funded is the normal state, so render nothing rather than an
  // empty-state card the operator has to read past on every customer.
  if (!apps || apps.length === 0) return null

  const revoke = async (app: FundedApp) => {
    const ok = await confirm({
      title: t('settings.platform.fundedApps.revokeTitle', { defaultValue: 'Withdraw funding?' }),
      message: t('settings.platform.fundedApps.revokeBody', {
        defaultValue:
          'This app will stop being paid for by this tenant. Anyone using it afterwards pays from their own tenant. The app keeps working.',
      }),
      type: 'warning',
      confirmText: t('settings.platform.fundedApps.revoke', { defaultValue: 'Withdraw funding' }),
    })
    if (!ok) return
    setBusy(true)
    try {
      await budgetApi.revokeAppFunding(tenantId, app.appFolderId)
      toast.success(t('settings.platform.fundedApps.revoked', { defaultValue: 'Funding withdrawn.' }))
      await load()
    } catch (e: any) {
      toast.error(e?.message || 'Failed to withdraw funding')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="themed-bg-secondary rounded-lg overflow-hidden mt-3">
      <div className="px-3 py-2 border-b themed-border">
        <div className="themed-text text-sm">
          {t('settings.platform.fundedApps.title', { defaultValue: 'Apps this tenant funds' })}
        </div>
        <p className="themed-text-muted text-[11px] mt-0.5">
          {t('settings.platform.fundedApps.desc', {
            defaultValue: "Usage by OTHER people, charged to this tenant because they opted in to pay for it.",
          })}
        </p>
      </div>
      {apps.map((app) => {
        const month = app.caps?.month
        return (
          <div key={app.id} className="px-3 py-2 border-b themed-border last:border-b-0 flex items-baseline justify-between gap-3">
            <div className="min-w-0">
              <span className="themed-text text-sm truncate">
                {app.appName ?? t('settings.platform.fundedApps.deleted', { defaultValue: 'Deleted app' })}
              </span>
              <div className="text-[11px] themed-text-muted font-mono truncate">{app.appFolderId}</div>
              <div className="text-[11px] themed-text-muted">
                {month === undefined || month === null
                  ? t('settings.platform.fundedApps.unlimited', { defaultValue: 'Funded, no ceiling' })
                  : month === 0
                    ? t('settings.platform.fundedApps.blocked', { defaultValue: 'Funded but blocked (0)' })
                    : t('settings.platform.fundedApps.ceiling', {
                        defaultValue: 'Ceiling {{cap}}', cap: formatMoney(month, moneyOpts),
                      })}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="themed-text text-sm tabular-nums">{formatMoney(app.periodCostBillingMicro, moneyOpts)}</div>
              {canEdit && (
                <button
                  onClick={() => revoke(app)}
                  disabled={busy}
                  className="mt-1 px-2 py-0.5 text-[11px] rounded themed-btn-secondary disabled:opacity-50"
                >
                  {t('settings.platform.fundedApps.revoke', { defaultValue: 'Withdraw funding' })}
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
